import React from 'react';
import * as enums from '@local/finsera-core/src/enums';
import { mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import DefCardContainer from './def-card-container';
import NumericField from './numeric-field';
import ContextSingleton from '../__singletons__/context-singleton';
import availableStrategies, { getId, StrategyOption } from '../data/available-strategies';

type StrategyOptionWithDisplay = StrategyOption & {
  displayName?: string;
};

type TCostModelProps = {
  definition: types.basket.DefinitionDraft;
  setDefinition: (_val: types.basket.DefinitionDraft) => void;
  readOnly?: boolean;
  type?: enums.BASKET_TYPE_ENUM;
  helpContent?: types.helpItem.HelpItemContent;
};

const NONE_ID = getId({ key: 'None', tcost_file_version: undefined } as StrategyOption);

const TCostModel = ({ setDefinition, definition, readOnly, type, helpContent }: TCostModelProps) => {
  const features = ContextSingleton.getInstance().features;

  const { options, selectedId } = React.useMemo(() => {
    const result = availableStrategies.filter((s: StrategyOption) => !s.deprecated);

    const currentTCostStrategy = definition.tcost_strategy;
    const currentVersion = definition.tcost_file_version;

    const currentStrategy = availableStrategies.find(
      (s: StrategyOption) =>
        getId(s) === getId({ key: currentTCostStrategy, tcost_file_version: currentVersion } as StrategyOption)
    );

    if (currentStrategy?.deprecated) result.push(currentStrategy);

    const newVersion = currentStrategy?.deprecated
      ? availableStrategies.find((s: StrategyOption) => s.type === currentStrategy.type && !s.deprecated)
      : undefined;

    const mapped = result.map((option: StrategyOption): StrategyOptionWithDisplay => {
      let displayName = option.name;
      if (option.deprecated) displayName = `${option.name} (deprecated)`;
      if (newVersion && getId(option) === getId(newVersion)) displayName = `${option.name} (new)`;
      return { ...option, displayName };
    });

    const currentId = definition.tcost_strategy
      ? getId({ key: definition.tcost_strategy, tcost_file_version: definition.tcost_file_version } as StrategyOption)
      : NONE_ID;

    return { options: mapped, selectedId: currentId };
  }, [definition.tcost_strategy, definition.tcost_file_version]);

  const handleStrategyChange = (selectedOption: StrategyOptionWithDisplay) => {
    const update: types.basket.DefinitionDraft = {
      ...definition,
      tcost_strategy: selectedOption.key,
      tcost_file_version: selectedOption.tcost_file_version,
    };

    if (definition.must_complete === undefined) update.must_complete = true;

    setDefinition(update);
  };

  const defaultHelpContent = helpContent || {
    helpLabel: type == enums.BASKET_TYPE_ENUM.COMBINED ? 'deuvr5qu' : 'deuvr5qc',
    document: enums.UI_RESOURCE_TYPE_ENUM.BASKET,
    contentPath: '/definition' as const,
    contentTitle:
      type == enums.BASKET_TYPE_ENUM.COMBINED ? 'Transaction Cost Model (Combined)' : 'Transaction Cost Model (Common)',
  };

  const selectedOption = options.find((o) => getId(o) === selectedId) || undefined;

  const showMustComplete =
    !!definition.tcost_strategy && definition.tcost_strategy !== 'None' && definition.tcost_strategy !== 'Fixed';

  const isOptionDisabled = (option: StrategyOptionWithDisplay) => !(features.optimizer_tcost || option.always_enabled);

  return (
    <DefCardContainer title="Transaction Cost Model" helpContent={defaultHelpContent}>
      <mui.core.ToggleButtonGroup
        exclusive
        value={selectedId}
        onChange={(_, value) => {
          if (!value) return;
          const next = options.find((s) => getId(s) === value);
          if (!next) return;
          if (isOptionDisabled(next)) return;
          handleStrategyChange(next);
        }}
        disabled={readOnly}
        sx={{ mb: 4 }}
      >
        {options.map((option) => (
          <mui.core.ToggleButton
            key={getId(option)}
            value={getId(option)}
            disabled={readOnly || isOptionDisabled(option)}
          >
            {option.displayName || option.name}
          </mui.core.ToggleButton>
        ))}
      </mui.core.ToggleButtonGroup>

      <mui.core.Stack flexDirection="row" gap={4}>
        {selectedOption?.key === 'None' ? null : null}
        <NumericField
          disabled={readOnly}
          textFieldProps={{
            label: 'TCost Adjustment Factor',
            fullWidth: true,
            variant: 'outlined',
            size: 'small',
          }}
          value={definition.tcost_multiplier}
          setValue={(v) => setDefinition({ ...definition, tcost_multiplier: v })}
        />

        {showMustComplete ? (
          <mui.core.Grid size={{ xs: 12, md: 4 }}>
            <mui.core.FormControlLabel
              disabled={readOnly}
              control={
                <mui.core.Switch
                  size="small"
                  checked={definition.must_complete as boolean}
                  onChange={() => setDefinition({ ...definition, must_complete: !definition.must_complete })}
                />
              }
              label="Must Complete"
            />
          </mui.core.Grid>
        ) : null}
      </mui.core.Stack>
    </DefCardContainer>
  );
};

export default TCostModel;
